import { runEngineUpdateQueue, type EngineUpdateOutcome as Outcome } from './engine-update-queue';
import { useEffect, useRef, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Download, ExternalLink } from 'lucide-react';
import { engineApi, type Store, type EngineThemeStatus } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { Modal } from '../ui/Modal';
import { Button, buttonVariants } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../EmptyState';

function getEngineAdminUrl(storeUrl: string): string | null {
  try {
    const url = new URL(storeUrl);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    url.pathname = `${url.pathname.replace(/\/$/, '')}/wp-admin/admin.php`;
    url.search = '?page=ecomflow-engine';
    url.hash = '';
    return url.toString();
  } catch { return null; }
}

const isConfigured = (store: Store) => Boolean(store.engineEnabled && store.engineIntegrationId);

export function useEngineUpdates(stores: Store[]) {
  const tenantId = useAuthStore(state => state.tenant?.id);
  const queryClient = useQueryClient();
  const key = (id: string) => ['engine-theme-status', tenantId, id];
  const queries = useQueries({ queries: stores.map(store => ({
    queryKey: key(store.engineIntegrationId ?? store.id),
    queryFn: async () => (await engineApi.getThemeStatus(store.engineIntegrationId!)).data,
    enabled: isConfigured(store), staleTime: 60_000, retry: false,
  })) });
  const [results, setResults] = useState<Record<string, Outcome>>({});
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const statuses = Object.fromEntries(stores.map((store, i) => [store.id, queries[i].isError ? { status: 'unknown' as const, latestVersion: '', message: 'Nie udało się sprawdzić wersji Engine. Spróbuj ponownie.' } : queries[i].data]));
  const checking = Object.fromEntries(stores.map((store, i) => [store.id, isConfigured(store) && queries[i].isFetching]));
  const setResult = (id: string, result: Outcome) => {
    if (mounted.current) setResults(prev => ({ ...prev, [id]: result }));
  };
  const run = async (targets: Store[]) => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setResults(previous => ({ ...Object.fromEntries(Object.entries(previous).filter(([id]) => stores.some(store => store.id === id))),
      ...Object.fromEntries(targets.map(store => [store.id, { state: 'queued' as const, message: 'W kolejce' }])) }));
    try {
      await runEngineUpdateQueue(targets.map(store => ({
        id: store.id, integrationId: isConfigured(store) ? store.engineIntegrationId : null,
        current: statuses[store.id]?.status === 'current',
      })), {
        isActive: () => mounted.current,
        update: async id => (await engineApi.updateTheme(id)).data,
        verify: async id => {
          await queryClient.invalidateQueries({ queryKey: key(id), refetchType: 'none' });
          return queryClient.fetchQuery({ queryKey: key(id),
            queryFn: async () => (await engineApi.getThemeStatus(id)).data, staleTime: 0, retry: false });
        },
        report: setResult,
      });
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const refresh = async () => {
    // Reopening during an update must not erase its progress or result.
    if (locked.current) return;
    setResults({});
    await queryClient.invalidateQueries({ queryKey: ['engine-theme-status', tenantId] });
  };
  return { statuses, checking, results, busy, run, refresh };
}

type Updates = ReturnType<typeof useEngineUpdates>;
export function EngineStatusBadge({ store, status, checking }: { store: Store; status?: EngineThemeStatus; checking?: boolean }) {
  const label = !isConfigured(store) ? 'Brak' : checking ? 'Sprawdzanie' : status?.status === 'current' ? 'Aktualny' : status?.status === 'update' ? 'Aktualizuj' : 'Nieznany';
  const color = label === 'Aktualny' ? 'engine-badge-current' : label === 'Aktualizuj' ? 'engine-badge-update' : '';
  return <Badge size="sm" className={color} title={status?.message ?? (status?.currentVersion ? `Zainstalowano: ${status.currentVersion}. Dostępna: ${status.latestVersion}.` : !isConfigured(store) ? 'Engine nie jest włączony dla tego sklepu.' : 'Odczyt wersji Engine')}>
    Engine: {label}
  </Badge>;
}

export function EngineUpdatesModal({ isOpen, onClose, stores, updates }: {
  isOpen: boolean; onClose: () => void; stores: Store[]; updates: Updates;
}) {
  const role = useAuthStore(state => state.user?.role);
  const isChecking = Object.values(updates.checking).some(Boolean);
  const canUpdate = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const counts = Object.values(updates.results).reduce((acc, result) => ({ ...acc, [result.state]: (acc[result.state] ?? 0) + 1 }), {} as Partial<Record<Outcome['state'], number>>);
  const download = async () => {
    setDownloading(true); setDownloadError('');
    try {
      const { data } = await engineApi.downloadTheme();
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'ecomflow-engine.zip'; anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setDownloadError('Nie udało się pobrać paczki Engine. Spróbuj ponownie.'); }
    finally { setDownloading(false); }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Aktualizacje Engine" size="xl"
    description="Sklepy aktualizują się po kolei. Błąd jednego sklepu nie zatrzymuje pozostałych."
    footer={<div className="flex flex-wrap items-center gap-3">
      <Button leftIcon={<RefreshCw />} loading={updates.busy} disabled={!canUpdate || isChecking || !stores.some(store => isConfigured(store) && updates.statuses[store.id]?.status !== 'current')}
        onClick={() => void updates.run(stores)}>{updates.busy ? 'Aktualizowanie...' : 'Zaktualizuj wszystkie'}</Button>
      <Button variant="ghost" onClick={onClose}>Zamknij</Button>
    </div>}>
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">Sklep może być przez chwilę niedostępny. Pozostań na tej stronie do zakończenia kolejki. Możesz zamknąć i ponownie otworzyć ten panel.</p>
      {!canUpdate && <p className="text-sm text-warning">Aktualizację może uruchomić administrator.</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" disabled={updates.busy} loading={isChecking} leftIcon={<RefreshCw />} onClick={() => void updates.refresh()}>Sprawdź wersje</Button>
        <Button variant="ghost" size="sm" loading={downloading} leftIcon={<Download />} onClick={() => void download()}>Pobierz paczkę</Button>
      </div>
      {downloadError && <p role="alert" className="text-sm text-danger">{downloadError}</p>}
      {Object.keys(updates.results).length > 0 && <p role="status" className="rounded-lg border border-border bg-surface-muted p-3 text-sm text-text-primary">
        {updates.busy ? 'Trwa aktualizacja. ' : 'Zakończono. '}
        Sukces: {counts.success ?? 0} · Błędy: {counts.error ?? 0} · Do sprawdzenia: {counts.unverified ?? 0} · Pominięte: {counts.skipped ?? 0} · Pozostało: {(counts.queued ?? 0) + (counts.running ?? 0)}
      </p>}
      {stores.length === 0 ? <EmptyState icon={RefreshCw} size="sm" title="Brak sklepów" description="Dodaj sklep, aby zarządzać aktualizacjami Engine." /> :
        <ul className="divide-y divide-border-subtle">{stores.map(store => {
          const result = updates.results[store.id];
          const status = updates.statuses[store.id];
          const manualUrl = getEngineAdminUrl(store.url);
          const needsManualUpdate = !updates.busy && !updates.checking[store.id] && isConfigured(store)
            && (result?.state === 'error' || result?.state === 'unverified' || !status || status.status === 'unknown');
          return <li key={store.id} className="py-4 flex gap-3 items-start">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap gap-2 items-center"><span className="font-medium text-text-primary break-words">{store.name}</span><EngineStatusBadge store={store} status={updates.statuses[store.id]} checking={updates.checking[store.id]} /></div>
              {updates.statuses[store.id]?.currentVersion && <p className="text-xs text-text-muted mt-1">{updates.statuses[store.id]?.currentVersion} → {updates.statuses[store.id]?.latestVersion}</p>}
              <p className={`text-sm mt-1 break-words ${result?.state === 'error' ? 'text-danger' : result?.state === 'unverified' ? 'text-warning' : 'text-text-secondary'}`}>
                {result?.message ?? updates.statuses[store.id]?.message}
              </p>
              {needsManualUpdate && manualUrl && <a href={manualUrl} target="_blank" rel="noopener noreferrer"
                className={`${buttonVariants({ variant: 'secondary', size: 'sm' })} mt-2`}
                aria-label={`Ręczna aktualizacja Engine: ${store.name} (nowa karta)`}>
                <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />Ręczna aktualizacja
              </a>}
            </div>
            <Button variant="icon" size="sm" aria-label={`Aktualizuj Engine: ${store.name}`} title={`Aktualizuj Engine: ${store.name}`} leftIcon={<RefreshCw />}
              loading={result?.state === 'running'} disabled={!canUpdate || updates.busy || isChecking || !isConfigured(store) || updates.statuses[store.id]?.status === 'current'} onClick={() => void updates.run([store])} />
          </li>;
        })}</ul>}
      <p className="text-xs text-text-muted">Jeśli starszy Engine zgłasza błąd krytyczny, zaktualizuj go jednorazowo w WordPress: EcomFlow → Aktualizacje. Możesz też pobrać paczkę i zastąpić motyw w Wygląd → Motywy.</p>
    </div>
  </Modal>;
}
